import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Snackbar from "@material-ui/core/Snackbar";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import useAlertSound from "../../hooks/useAlertSound";
import alertSound from "../../assets/sound.mp3";

/**
 * Aviso de compromisso próximo.
 *
 * Fica montado no layout autenticado, e não na página da agenda: o aviso só
 * serve se alcançar quem está atendendo um ticket ou olhando o painel — quem
 * já está na agenda é justamente quem menos precisa dele.
 *
 * Aparece no canto e **não some sozinho**. Um aviso de dez minutos que
 * desaparece em cinco segundos é o mesmo que não avisar; quem estava numa
 * ligação perde. Sai quando a pessoa fecha ou abre a agenda.
 *
 * O evento de socket sozinho não basta: ele existe por um instante, e quem
 * estava com a aba fechada, em outra guia ou com a conexão reconectando não
 * recebe nada — sem qualquer rastro de que houve um aviso. Por isso a tela
 * também **pergunta ao servidor o que está para começar** ao abrir e ao
 * reconectar. O que a pessoa já dispensou fica guardado no navegador, para o
 * aviso não voltar a cada recarga.
 */
const useStyles = makeStyles((theme) => ({
  content: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[8],
    minWidth: 320,
    maxWidth: 400,
    alignItems: "flex-start",
  },
  corpo: { display: "flex", gap: theme.spacing(1.5), alignItems: "flex-start" },
  icone: { color: theme.palette.primary.main, marginTop: 2 },
  quando: { fontWeight: 700, display: "block" },
  titulo: { fontWeight: 600, marginTop: 2 },
  detalhe: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 2,
    color: theme.palette.text.secondary,
  },
}));

const AppointmentReminder = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [aviso, setAviso] = useState(null);
  const tocar = useAlertSound(alertSound);

  // Dispensados ficam no navegador: recarregar a página não deve trazer de
  // volta um aviso que a pessoa já leu.
  const jaDispensado = (id) => {
    try {
      const lista = JSON.parse(localStorage.getItem("avisosAgendaLidos") || "[]");
      return lista.includes(id);
    } catch (err) {
      return false;
    }
  };

  const dispensar = (id) => {
    try {
      const lista = JSON.parse(localStorage.getItem("avisosAgendaLidos") || "[]");
      // Só os últimos 50: a lista não pode crescer para sempre.
      const nova = [...new Set([...lista, id])].slice(-50);
      localStorage.setItem("avisosAgendaLidos", JSON.stringify(nova));
    } catch (err) {
      /* sem localStorage o aviso apenas volta na próxima carga */
    }
    setAviso(null);
  };

  // Recupera o que está para começar agora — cobre o tempo em que a tela
  // esteve fechada.
  const buscarPendentes = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments/reminders/pending");
      const pendente = (data || []).find((a) => !jaDispensado(a.id));
      if (pendente) setAviso((atual) => atual || pendente);
    } catch (err) {
      /* silencioso: é recuperação, não o caminho principal */
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    buscarPendentes();
    // Reconferimos de tempos em tempos: a aba pode ficar aberta o dia todo com
    // o socket caindo e voltando sem ninguém perceber.
    const id = setInterval(buscarPendentes, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [user, buscarPendentes]);

  const aoReceber = useCallback(
    (data) => {
      // Compromisso é pessoal: o servidor transmite para todos e cada tela
      // mostra só o que é de quem está logado nela.
      if (!data?.appointment) return;
      if (data.userId && user?.id && Number(data.userId) !== Number(user.id)) {
        return;
      }

      if (jaDispensado(data.appointment.id)) return;

      setAviso(data.appointment);
      try {
        tocar();
      } catch (err) {
        /* som é acessório */
      }
    },
    [user, tocar]
  );

  useSocketSubscription("appointment:reminder", aoReceber, [user?.id]);

  if (!aviso) return null;

  const abrirAgenda = () => {
    dispensar(aviso.id);
    history.push("/agenda");
  };

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      // Sem autoHideDuration de propósito — ver comentário do componente.
    >
      <SnackbarContent
        className={classes.content}
        message={
          <div className={classes.corpo}>
            <EventAvailableOutlinedIcon className={classes.icone} />
            <div>
              <Typography variant="caption" color="primary" className={classes.quando}>
                {aviso.message}
              </Typography>
              <Typography variant="body2" className={classes.titulo}>
                {aviso.title}
              </Typography>
              {aviso.clientName && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {aviso.clientName}
                </Typography>
              )}
              {aviso.location && (
                <div className={classes.detalhe}>
                  <RoomOutlinedIcon style={{ fontSize: 14 }} />
                  <Typography variant="caption">{aviso.location}</Typography>
                </div>
              )}
            </div>
          </div>
        }
        action={[
          <Button key="ver" size="small" color="primary" onClick={abrirAgenda}>
            Ver agenda
          </Button>,
          <IconButton key="fechar" size="small" onClick={() => dispensar(aviso.id)}>
            <CloseIcon fontSize="small" />
          </IconButton>,
        ]}
      />
    </Snackbar>
  );
};

export default AppointmentReminder;
